import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'
import { z } from 'zod/v4'

import {
	Activities,
	CallLogs,
	CallRecordings,
	Connections,
	DataSources,
	DataSourceVersions,
	Domains,
	GridDataSources,
	Grids,
	GridVersionDataSources,
	GridVersions,
	Locations,
	OpeningHoursPeriods,
	PhoneNumbers,
	Photos,
	Reviews,
	SmsMessages,
	WebhookEvents,
} from '../schema/WorkspaceDOSchema'

export const WorkspaceStatusValidator = z.enum(['active', 'archived'])
export const WorkspaceRoleValidator = z.enum(['owner', 'editor', 'viewer'])

// External workspace validator (not part of WorkspaceDO schema)
export const WorkspaceValidator = z.object({
	id: z.number(),
	uuid: z.string(),
	account_id: z.string(),
	background_image: z.string().nullable(),
	created_at: z.string().datetime(),
	name: z.string(),
	status: WorkspaceStatusValidator,
	campaign_count: z.number(),
	connection_count: z.number(),
	grid_count: z.number(),
	landing_page_count: z.number(),
	location_count: z.number(),
})

export const BaseWorkspaceValidator = WorkspaceValidator.omit({
	id: true,
	uuid: true,
	created_at: true,
}).partial()

export const WorkspacePermissionsValidator = z.object({
	id: z.number(),
	uuid: z.string(),
	workspace_id: z.number(),
	user_id: z.string().optional(),
	email: z.string(),
	role: WorkspaceRoleValidator,
	invitation_status: z.enum(['pending', 'accepted']).default('pending'),
	created_at: z.string().datetime(),
})

export const BaseWorkspacePermissionsValidator = WorkspacePermissionsValidator.omit({
	id: true,
	uuid: true,
	created_at: true,
})

export const InviteUserValidator = z.object({
	email: z.string(),
	role: WorkspaceRoleValidator,
})

// === Drizzle-generated validators for WorkspaceDO schema tables ===

// Grids
export const GridSelectValidator = createSelectSchema(Grids)
export const GridInsertValidator = createInsertSchema(Grids)
export const GridUpdateValidator = createUpdateSchema(Grids)

// GridVersions
export const GridVersionSelectValidator = createSelectSchema(GridVersions)
export const GridVersionInsertValidator = createInsertSchema(GridVersions)
export const GridVersionUpdateValidator = createUpdateSchema(GridVersions)

// Locations
export const LocationSelectValidator = createSelectSchema(Locations, {
	types: z.array(z.string()), // Refine JSON field to be more specific
})
export const LocationInsertValidator = createInsertSchema(Locations, {
	types: z.array(z.string()), // Refine JSON field to be more specific
})
export const LocationUpdateValidator = createUpdateSchema(Locations, {
	types: z.array(z.string()), // Refine JSON field to be more specific
})

// OpeningHoursPeriods
export const OpeningHoursPeriodsSelectValidator = createSelectSchema(OpeningHoursPeriods)
export const OpeningHoursPeriodsInsertValidator = createInsertSchema(OpeningHoursPeriods)
export const OpeningHoursPeriodsUpdateValidator = createUpdateSchema(OpeningHoursPeriods)

// Photos
export const PhotoSelectValidator = createSelectSchema(Photos)
export const PhotoInsertValidator = createInsertSchema(Photos)
export const PhotoUpdateValidator = createUpdateSchema(Photos)

// Reviews
export const ReviewSelectValidator = createSelectSchema(Reviews)
export const ReviewInsertValidator = createInsertSchema(Reviews)
export const ReviewUpdateValidator = createUpdateSchema(Reviews)

// Connections
export const ConnectionSelectValidator = createSelectSchema(Connections, {
	auth_data: z.record(z.string(), z.any()), // Refine JSON field
	platform_data: z.record(z.string(), z.any()).optional(), // Refine JSON field
	settings: z.record(z.string(), z.any()).optional(), // Refine JSON field
})
export const ConnectionInsertValidator = createInsertSchema(Connections, {
	auth_data: z.record(z.string(), z.any()), // Refine JSON field
	platform_data: z.record(z.string(), z.any()).optional(), // Refine JSON field
	settings: z.record(z.string(), z.any()).optional(), // Refine JSON field
})
export const ConnectionUpdateValidator = createUpdateSchema(Connections, {
	auth_data: z.record(z.string(), z.any()).optional(), // Refine JSON field
	platform_data: z.record(z.string(), z.any()).optional(), // Refine JSON field
	settings: z.record(z.string(), z.any()).optional(), // Refine JSON field
})

// DataSources
export const DataSourceSelectValidator = createSelectSchema(DataSources)
export const DataSourceInsertValidator = createInsertSchema(DataSources)
export const DataSourceUpdateValidator = createUpdateSchema(DataSources)

// DataSourceVersions
export const DataSourceVersionSelectValidator = createSelectSchema(DataSourceVersions, {
	metadata: z.record(z.string(), z.any()).optional(), // Refine JSON field
})
export const DataSourceVersionInsertValidator = createInsertSchema(DataSourceVersions, {
	metadata: z.record(z.string(), z.any()).optional(), // Refine JSON field
})
export const DataSourceVersionUpdateValidator = createUpdateSchema(DataSourceVersions, {
	metadata: z.record(z.string(), z.any()).optional(), // Refine JSON field
})

// GridDataSources
export const GridDataSourceSelectValidator = createSelectSchema(GridDataSources)
export const GridDataSourceInsertValidator = createInsertSchema(GridDataSources)
export const GridDataSourceUpdateValidator = createUpdateSchema(GridDataSources)

// GridVersionDataSources
export const GridVersionDataSourceSelectValidator = createSelectSchema(GridVersionDataSources)
export const GridVersionDataSourceInsertValidator = createInsertSchema(GridVersionDataSources)
export const GridVersionDataSourceUpdateValidator = createUpdateSchema(GridVersionDataSources)

// === Phone Number Capabilities ===

/**
 * Full Twilio IncomingPhoneNumber capabilities (from Twilio API responses)
 * Use this for validating Twilio API responses
 */
export const TwilioIncomingPhoneNumberCapabilitiesValidator = z.object({
	voice: z.boolean().describe('Whether the phone number can handle voice calls'),
	sms: z.boolean().describe('Whether the phone number can send/receive SMS messages'),
	mms: z.boolean().describe('Whether the phone number can send/receive MMS messages'),
	fax: z.boolean().describe('Whether the phone number can send/receive fax messages'),
})

// PhoneNumbers - drizzle-zod will automatically infer the capabilities type from the schema
export const PhoneNumberSelectValidator = createSelectSchema(PhoneNumbers)
export const PhoneNumberInsertValidator = createInsertSchema(PhoneNumbers)
export const PhoneNumberUpdateValidator = createUpdateSchema(PhoneNumbers)

// Domains
export const DomainSelectValidator = createSelectSchema(Domains, {
	dns_records: z.record(z.string(), z.any()).optional(), // Refine JSON field
})
export const DomainInsertValidator = createInsertSchema(Domains, {
	dns_records: z.record(z.string(), z.any()).optional(), // Refine JSON field
})
export const DomainUpdateValidator = createUpdateSchema(Domains, {
	dns_records: z.record(z.string(), z.any()).optional(), // Refine JSON field
})

// CallLogs
export const CallLogSelectValidator = createSelectSchema(CallLogs)
export const CallLogInsertValidator = createInsertSchema(CallLogs)
export const CallLogUpdateValidator = createUpdateSchema(CallLogs)

// CallRecordings
export const CallRecordingSelectValidator = createSelectSchema(CallRecordings)
export const CallRecordingInsertValidator = createInsertSchema(CallRecordings)
export const CallRecordingUpdateValidator = createUpdateSchema(CallRecordings)

// SmsMessages
export const SmsMessageSelectValidator = createSelectSchema(SmsMessages, {
	media_urls: z.array(z.string()).optional(), // Refine JSON field
})
export const SmsMessageInsertValidator = createInsertSchema(SmsMessages, {
	media_urls: z.array(z.string()).optional(), // Refine JSON field
})
export const SmsMessageUpdateValidator = createUpdateSchema(SmsMessages, {
	media_urls: z.array(z.string()).optional(), // Refine JSON field
})

// WebhookEvents
export const WebhookEventSelectValidator = createSelectSchema(WebhookEvents, {
	payload: z.record(z.string(), z.any()), // Refine JSON field
})
export const WebhookEventInsertValidator = createInsertSchema(WebhookEvents, {
	payload: z.record(z.string(), z.any()), // Refine JSON field
})
export const WebhookEventUpdateValidator = createUpdateSchema(WebhookEvents, {
	payload: z.record(z.string(), z.any()), // Refine JSON field
})

// Activities
export const ActivitySelectValidator = createSelectSchema(Activities)
export const ActivityInsertValidator = createInsertSchema(Activities)
export const ActivityUpdateValidator = createUpdateSchema(Activities)

export type WorkspaceId = z.infer<typeof WorkspaceValidator.shape.uuid>
export type WorkspaceStatus = z.infer<typeof WorkspaceStatusValidator>
export type WorkspaceRole = z.infer<typeof WorkspaceRoleValidator>
export type Workspace = z.infer<typeof WorkspaceValidator>
export type BaseWorkspace = z.infer<typeof BaseWorkspaceValidator>
export type WorkspacePermission = z.infer<typeof WorkspacePermissionsValidator>
export type BaseWorkspacePermission = z.infer<typeof BaseWorkspacePermissionsValidator>
export type InviteUser = z.infer<typeof InviteUserValidator>

// === Types from Drizzle schema inference (for database operations) ===
// Use these for actual database operations to ensure compatibility

export type WorkspaceGrid = typeof Grids.$inferSelect
export type WorkspaceGridInsert = typeof Grids.$inferInsert
export type WorkspaceGridUpdate = Partial<WorkspaceGridInsert>

export type WorkspaceGridVersion = typeof GridVersions.$inferSelect
export type WorkspaceGridVersionInsert = typeof GridVersions.$inferInsert
export type WorkspaceGridVersionUpdate = Partial<WorkspaceGridVersionInsert>

export type WorkspaceLocation = typeof Locations.$inferSelect
export type WorkspaceLocationInsert = typeof Locations.$inferInsert
export type WorkspaceLocationUpdate = Partial<WorkspaceLocationInsert>

export type WorkspaceOpeningHoursPeriod = typeof OpeningHoursPeriods.$inferSelect
export type WorkspaceOpeningHoursPeriodsInsert = typeof OpeningHoursPeriods.$inferInsert
export type WorkspaceOpeningHoursPeriodsUpdate = Partial<WorkspaceOpeningHoursPeriodsInsert>

export type WorkspacePhoto = typeof Photos.$inferSelect
export type WorkspacePhotoInsert = typeof Photos.$inferInsert
export type WorkspacePhotoUpdate = Partial<WorkspacePhotoInsert>

export type WorkspaceReview = typeof Reviews.$inferSelect
export type WorkspaceReviewInsert = typeof Reviews.$inferInsert
export type WorkspaceReviewUpdate = Partial<WorkspaceReviewInsert>

export type Connection = typeof Connections.$inferSelect
export type ConnectionInsert = typeof Connections.$inferInsert
export type ConnectionUpdate = Partial<ConnectionInsert>

export type WorkspaceDataSource = typeof DataSources.$inferSelect
export type WorkspaceDataSourceInsert = typeof DataSources.$inferInsert
export type WorkspaceDataSourceUpdate = Partial<WorkspaceDataSourceInsert>

export type DataSourceVersion = typeof DataSourceVersions.$inferSelect
export type DataSourceVersionInsert = typeof DataSourceVersions.$inferInsert
export type DataSourceVersionUpdate = Partial<DataSourceVersionInsert>

export type GridDataSource = typeof GridDataSources.$inferSelect
export type GridDataSourceInsert = typeof GridDataSources.$inferInsert
export type GridDataSourceUpdate = Partial<GridDataSourceInsert>

export type WorkspaceGridVersionDataSource = typeof GridVersionDataSources.$inferSelect
export type WorkspaceGridVersionDataSourceInsert = typeof GridVersionDataSources.$inferInsert
export type WorkspaceGridVersionDataSourceUpdate = Partial<WorkspaceGridVersionDataSourceInsert>

export type TwilioIncomingPhoneNumberCapabilities = z.infer<
	typeof TwilioIncomingPhoneNumberCapabilitiesValidator
>
// PhoneNumberCapabilities type is exported from the schema (WorkspaceDOSchema.ts)
export type PhoneNumber = typeof PhoneNumbers.$inferSelect
export type PhoneNumberInsert = typeof PhoneNumbers.$inferInsert
export type PhoneNumberUpdate = Partial<PhoneNumberInsert>

export type WorkspaceDomain = typeof Domains.$inferSelect
export type WorkspaceDomainInsert = typeof Domains.$inferInsert
export type WorkspaceDomainUpdate = Partial<WorkspaceDomainInsert>

export type CallLog = typeof CallLogs.$inferSelect
export type CallLogInsert = typeof CallLogs.$inferInsert
export type CallLogUpdate = Partial<CallLogInsert>

export type CallRecording = typeof CallRecordings.$inferSelect
export type CallRecordingInsert = typeof CallRecordings.$inferInsert
export type CallRecordingUpdate = Partial<CallRecordingInsert>

export type SmsMessage = typeof SmsMessages.$inferSelect
export type SmsMessageInsert = typeof SmsMessages.$inferInsert
export type SmsMessageUpdate = Partial<SmsMessageInsert>

export type WebhookEvent = typeof WebhookEvents.$inferSelect
export type WebhookEventInsert = typeof WebhookEvents.$inferInsert
export type WebhookEventUpdate = Partial<WebhookEventInsert>

export type Activity = typeof Activities.$inferSelect
export type ActivityInsert = typeof Activities.$inferInsert
export type ActivityUpdate = Partial<ActivityInsert>

// === Validator-inferred types (for API validation) ===
// Use these for API request/response validation

export type GridValidated = z.infer<typeof GridSelectValidator>
export type GridInsertValidated = z.infer<typeof GridInsertValidator>
export type GridUpdateValidated = z.infer<typeof GridUpdateValidator>

export type LocationValidated = z.infer<typeof LocationSelectValidator>
export type LocationInsertValidated = z.infer<typeof LocationInsertValidator>
export type LocationUpdateValidated = z.infer<typeof LocationUpdateValidator>

export type ConnectionValidated = z.infer<typeof ConnectionSelectValidator>
export type ConnectionInsertValidated = z.infer<typeof ConnectionInsertValidator>
export type ConnectionUpdateValidated = z.infer<typeof ConnectionUpdateValidator>

export type PhoneNumberValidated = z.infer<typeof PhoneNumberSelectValidator>
export type PhoneNumberInsertValidated = z.infer<typeof PhoneNumberInsertValidator>
export type PhoneNumberUpdateValidated = z.infer<typeof PhoneNumberUpdateValidator>

export type DomainValidated = z.infer<typeof DomainSelectValidator>
export type DomainInsertValidated = z.infer<typeof DomainInsertValidator>
export type DomainUpdateValidated = z.infer<typeof DomainUpdateValidator>

export type CallLogValidated = z.infer<typeof CallLogSelectValidator>
export type CallLogInsertValidated = z.infer<typeof CallLogInsertValidator>
export type CallLogUpdateValidated = z.infer<typeof CallLogUpdateValidator>

export type CallRecordingValidated = z.infer<typeof CallRecordingSelectValidator>
export type CallRecordingInsertValidated = z.infer<typeof CallRecordingInsertValidator>
export type CallRecordingUpdateValidated = z.infer<typeof CallRecordingUpdateValidator>

export type SmsMessageValidated = z.infer<typeof SmsMessageSelectValidator>
export type SmsMessageInsertValidated = z.infer<typeof SmsMessageInsertValidator>
export type SmsMessageUpdateValidated = z.infer<typeof SmsMessageUpdateValidator>

export type WebhookEventValidated = z.infer<typeof WebhookEventSelectValidator>
export type WebhookEventInsertValidated = z.infer<typeof WebhookEventInsertValidator>
export type WebhookEventUpdateValidated = z.infer<typeof WebhookEventUpdateValidator>

export type ActivityValidated = z.infer<typeof ActivitySelectValidator>
export type ActivityInsertValidated = z.infer<typeof ActivityInsertValidator>
export type ActivityUpdateValidated = z.infer<typeof ActivityUpdateValidator>

// Asset types that were in Workspace.ts
export enum AssetTypes {
	IMAGE = 'image',
	VIDEO = 'video',
	HEADLINE = 'headline',
	DESCRIPTION = 'description',
	CTA = 'cta',
}

export const IntegrationValidator = z.object({
	platform: z.enum(['google', 'facebook']),
	refresh_token: z.string(),
	access_token: z.string(),
})

export const AssetLibraryValidator = z.object({
	id: z.string().optional(),
	name: z.string(),
	headlines: z.array(z.string()),
})

export const AssetValidator = z.object({
	id: z.string(),
	url: z.string(),
	type: z.nativeEnum(AssetTypes),
	tags: z.array(z.string()),
})

export type Integration = z.infer<typeof IntegrationValidator>
export type AssetLibrary = z.infer<typeof AssetLibraryValidator>
export type Asset = z.infer<typeof AssetValidator>
